import { useEffect, useState } from 'react';
import { Link, useOutletContext, useParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { extractErrorMessage } from '@/pages/painel-pedidos/lib/errors';
import TimelineComunicacoes from '@/pages/painel-pedidos/components/TimelineComunicacoes';
import ClienteObrasSection from '@/pages/painel/components/ClienteObrasSection';
import ClienteRadarSection from '@/pages/painel/components/ClienteRadarSection';
import InteligenciaRelacionamento from '@/pages/painel/components/InteligenciaRelacionamento';
import { PAPEL_EMPRESA_LABELS, ORIGEM_LABELS, NIVEL_DECISAO_LABELS } from '@/pages/painel/lib/comercial';
import {
  ATIVIDADE_TIPOS,
  CRM_ETAPAS,
  CRM_SCORE_TONES,
  CRM_TIPO_LABELS,
  type AtividadeRow,
  type ContatoRow,
  type EmpresaRow,
  type OportunidadeRow,
} from '@/pages/painel-pedidos/types';

interface Detalhe {
  empresa: EmpresaRow;
  atividades: AtividadeRow[];
  quotes: any[];
  orders: any[];
}

const FUTURO_MODULOS: { label: string; icon: string; desc: string }[] = [
  { label: 'Faturamento & lucro', icon: 'ri-money-dollar-circle-line', desc: 'Receita e margem geradas por este cliente.' },
  { label: 'Produtos/soluções comprados', icon: 'ri-archive-line', desc: 'Histórico consolidado de itens adquiridos.' },
  { label: 'Arquivos', icon: 'ri-folder-3-line', desc: 'Propostas, contratos e documentos anexos.' },
  { label: 'Arquitetos relacionados', icon: 'ri-pen-nib-line', desc: 'Profissionais ligados à conta.' },
  { label: 'Construtoras relacionadas', icon: 'ri-building-2-line', desc: 'Construtoras associadas (fora do contexto de uma obra específica).' },
  { label: 'Licitações (Lovable)', icon: 'ri-auction-line', desc: 'Integração futura com o app de licitações.' },
  { label: 'Origem do relacionamento', icon: 'ri-links-line', desc: 'Como o cliente chegou até a empresa.' },
];

function formatBRL(value: number | null | undefined): string {
  if (value == null) return '—';
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '';
  return new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function formatCNPJ(cnpj: string | null | undefined): string {
  if (!cnpj) return '—';
  const d = cnpj.replace(/\D/g, '');
  if (d.length !== 14) return cnpj;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
}

export default function ClienteDetail() {
  const { password } = useOutletContext<{ password: string }>();
  const { id } = useParams<{ id: string }>();

  const [detalhe, setDetalhe] = useState<Detalhe | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [activeTab, setActiveTab] = useState<'timeline' | 'comunicacoes' | 'propostas' | 'pedidos'>('timeline');

  // contatos
  const [newNome, setNewNome] = useState('');
  const [newCargo, setNewCargo] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newTelefone, setNewTelefone] = useState('');
  const [newWhatsapp, setNewWhatsapp] = useState('');
  const [newLinkedin, setNewLinkedin] = useState('');
  const [newDepartamento, setNewDepartamento] = useState('');
  const [newNivelDecisao, setNewNivelDecisao] = useState('');
  const [newObservacao, setNewObservacao] = useState('');
  const [addingContato, setAddingContato] = useState(false);

  // atividades
  const [newAtividade, setNewAtividade] = useState('');
  const [newTipo, setNewTipo] = useState('tarefa');
  const [addingAtividade, setAddingAtividade] = useState(false);
  const [sugerindo, setSugerindo] = useState(false);
  const [sugestao, setSugestao] = useState<any>(null);

  // papéis e origem (Conta 360º)
  const [editPapeis, setEditPapeis] = useState<string[]>([]);
  const [editOrigem, setEditOrigem] = useState('');
  const [savingPapeis, setSavingPapeis] = useState(false);

  const empresaId = Number(id);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('crm-core', {
        body: { action: 'get_empresa', password, empresa_id: empresaId },
      });
      if (invokeErr) throw new Error(await extractErrorMessage(invokeErr));
      const parsed = data as Detalhe & { error?: string };
      if (parsed?.error) throw new Error(parsed.error);
      setDetalhe(parsed);
      setEditPapeis(parsed?.empresa?.papeis ?? []);
      setEditOrigem(parsed?.empresa?.origem ?? '');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao carregar o cliente.');
    } finally {
      setLoading(false);
    }
  };

  const savePapeis = async () => {
    setSavingPapeis(true);
    try {
      await supabase.functions.invoke('comercial-admin', {
        body: { action: 'empresa_salvar', password, empresa_id: empresaId, papeis: editPapeis, origem: editOrigem || null },
      });
      await load();
    } catch {
      // silencioso
    } finally {
      setSavingPapeis(false);
    }
  };

  useEffect(() => {
    if (empresaId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaId, password]);

  const addContato = async () => {
    if (!newNome.trim()) return;
    setAddingContato(true);
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('comercial-admin', {
        body: {
          action: 'contato_salvar',
          password,
          empresa_id: empresaId,
          nome: newNome.trim(),
          cargo: newCargo.trim(),
          departamento: newDepartamento.trim(),
          email: newEmail.trim(),
          telefone: newTelefone.trim(),
          whatsapp: newWhatsapp.trim(),
          linkedin: newLinkedin.trim(),
          nivel_decisao: newNivelDecisao || null,
          observacao: newObservacao.trim(),
        },
      });
      if (invokeErr) throw invokeErr;
      const parsed = data as { error?: string };
      if (parsed?.error) throw new Error(parsed.error);
      setNewNome('');
      setNewCargo('');
      setNewDepartamento('');
      setNewEmail('');
      setNewTelefone('');
      setNewWhatsapp('');
      setNewLinkedin('');
      setNewNivelDecisao('');
      setNewObservacao('');
      await load();
    } catch {
      // silencioso
    } finally {
      setAddingContato(false);
    }
  };

  const moveEtapa = async (oportunidadeId: number, etapa: string) => {
    try {
      await supabase.functions.invoke('crm-core', {
        body: { action: 'update_oportunidade', password, oportunidade_id: oportunidadeId, etapa },
      });
      await load();
    } catch {
      // silencioso
    }
  };

  const addAtividade = async () => {
    if (!newAtividade.trim()) return;
    setAddingAtividade(true);
    try {
      await supabase.functions.invoke('crm-core', {
        body: {
          action: 'create_atividade',
          password,
          empresa_id: empresaId,
          tipo: newTipo,
          conteudo: newAtividade.trim(),
          data: new Date().toISOString(),
          prioridade: 'media',
        },
      });
      setNewAtividade('');
      await load();
    } catch {
      // silencioso
    } finally {
      setAddingAtividade(false);
    }
  };

  const toggleAtividade = async (atividadeId: number, done: boolean) => {
    try {
      await supabase.functions.invoke('crm-core', {
        body: { action: 'toggle_atividade', password, atividade_id: atividadeId, done },
      });
      await load();
    } catch {
      // silencioso
    }
  };

  const sugerirProximaAcao = async () => {
    setSugerindo(true);
    setSugestao(null);
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('crm-ai', {
        body: { action: 'proxima_acao', password, empresa_id: empresaId },
      });
      if (invokeErr) throw new Error(await extractErrorMessage(invokeErr));
      const parsed = data as any;
      if (parsed?.error) throw new Error(parsed.error);
      setSugestao(parsed);
    } catch {
      // silencioso
    } finally {
      setSugerindo(false);
    }
  };

  const agendarSugestao = async () => {
    if (!sugestao) return;
    const followUp = new Date();
    followUp.setDate(followUp.getDate() + (Number(sugestao.prazo_dias ?? 2) || 2));
    try {
      await supabase.functions.invoke('crm-core', {
        body: {
          action: 'create_atividade',
          password,
          empresa_id: empresaId,
          tipo: 'tarefa',
          conteudo: sugestao.proxima_acao ?? 'Próxima ação sugerida pela IA',
          data: followUp.toISOString(),
          follow_up_at: followUp.toISOString(),
          prioridade: 'alta',
          origem: 'ia',
        },
      });
      setSugestao(null);
      await load();
    } catch {
      // silencioso
    }
  };

  if (loading && !detalhe) {
    return (
      <div className="flex justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#E8610A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error && !detalhe) {
    return (
      <div className="space-y-4">
        <Link
          to="/painel/clientes"
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-left-line"></i>
          Voltar para clientes
        </Link>
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
          {error}
        </div>
      </div>
    );
  }

  if (!detalhe) return null;

  const { empresa, atividades, quotes, orders } = detalhe;
  const scoreInfo = CRM_SCORE_TONES[empresa.classificacao];
  const totalPedidos = orders.reduce((s, o) => s + Number(o.subtotal_items ?? 0), 0);

  const TAB_CONFIG = [
    { key: 'timeline' as const, label: 'Timeline', icon: 'ri-time-line', count: atividades.length },
    { key: 'comunicacoes' as const, label: 'Comunicações', icon: 'ri-mail-send-line', count: undefined },
    { key: 'propostas' as const, label: 'Propostas', icon: 'ri-file-list-3-line', count: quotes.length },
    { key: 'pedidos' as const, label: 'Pedidos', icon: 'ri-shopping-bag-3-line', count: orders.length },
  ];

  return (
    <div className="space-y-6">
      {/* Voltar */}
      <Link
        to="/painel/clientes"
        className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 cursor-pointer whitespace-nowrap"
      >
        <i className="ri-arrow-left-line"></i>
        Voltar para clientes
      </Link>

      {/* Cabeçalho */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-4 min-w-0">
            <div className="w-14 h-14 flex items-center justify-center bg-[#E8610A]/10 text-[#E8610A] rounded-2xl flex-shrink-0">
              <i className="ri-building-4-line text-2xl"></i>
            </div>
            <div className="min-w-0">
              <h2 className="text-xl font-bold text-gray-900">{empresa.nome}</h2>
              {empresa.razao_social && (
                <p className="text-sm text-gray-500">{empresa.razao_social}</p>
              )}
              <div className="flex items-center gap-1.5 flex-wrap mt-2">
                {scoreInfo && (
                  <span className={`inline-flex items-center gap-1 text-xs font-semibold rounded-full px-2.5 py-1 border ${scoreInfo.tone}`}>
                    <i className={scoreInfo.icon}></i>
                    {scoreInfo.label} · score {empresa.score}
                  </span>
                )}
                {empresa.tipo && (
                  <span className="text-xs font-semibold text-gray-600 bg-gray-100 rounded-full px-2.5 py-1">
                    {CRM_TIPO_LABELS[empresa.tipo] ?? empresa.tipo}
                  </span>
                )}
                {empresa.status === 'cliente' && (
                  <span className="text-xs font-semibold text-green-700 bg-green-50 border border-green-200 rounded-full px-2.5 py-1">
                    Cliente
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="text-right">
            <p className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold">Potencial</p>
            <p className="text-2xl font-bold text-[#E8610A]">{formatBRL(empresa.valor_potencial)}</p>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold">Score</p>
          <p className="text-2xl font-bold text-gray-900">{empresa.score}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold">Contatos</p>
          <p className="text-2xl font-bold text-gray-900">{empresa.contatos?.length ?? 0}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold">Oportunidades</p>
          <p className="text-2xl font-bold text-gray-900">{empresa.oportunidades?.length ?? 0}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold">Total em pedidos</p>
          <p className="text-lg font-bold text-gray-900 truncate">{formatBRL(totalPedidos)}</p>
        </div>
      </div>

      {/* Dados cadastrais */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <h3 className="text-sm font-bold text-gray-900 mb-4">Dados cadastrais</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { label: 'CNPJ', value: formatCNPJ(empresa.cnpj) },
            { label: 'Cidade / UF', value: [empresa.cidade, empresa.uf].filter(Boolean).join(' - ') || '—' },
            { label: 'Segmento', value: empresa.segmento || '—' },
            { label: 'Porte', value: empresa.porte || '—' },
            { label: 'Endereço', value: empresa.endereco || '—' },
            { label: 'Bairro', value: empresa.bairro || '—' },
            { label: 'E-mail geral', value: empresa.email_geral || '—' },
            { label: 'Site', value: empresa.site || '—' },
            { label: 'Fonte / origem', value: empresa.fonte || '—' },
            { label: 'Nível de confiança', value: empresa.nivel_confianca || '—' },
            { label: 'Descoberto em', value: formatDate(empresa.created_at) },
          ].map((c) => (
            <div key={c.label}>
              <p className="text-[11px] text-gray-400 uppercase tracking-wider font-semibold">{c.label}</p>
              <p className="text-sm text-gray-800 mt-0.5 break-words">{c.value}</p>
            </div>
          ))}
        </div>
        {empresa.resumo && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <p className="text-[11px] text-gray-400 uppercase tracking-wider font-semibold mb-1">Resumo</p>
            <p className="text-sm text-gray-700 leading-relaxed">{empresa.resumo}</p>
          </div>
        )}
      </div>

      {/* Papéis e origem (Conta 360º) */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
          <div>
            <h3 className="text-sm font-bold text-gray-900">Papéis da conta e origem</h3>
            <p className="text-xs text-gray-500">Uma empresa pode ter vários papéis. Não criar duplicata por papel.</p>
          </div>
          <button
            onClick={savePapeis}
            disabled={savingPapeis}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-[#E8610A] hover:bg-[#C4500A] rounded-lg px-3 py-2 cursor-pointer whitespace-nowrap disabled:opacity-60"
          >
            {savingPapeis ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-line"></i>}
            Salvar
          </button>
        </div>

        <div className="flex items-center gap-1.5 flex-wrap mb-4">
          {Object.entries(PAPEL_EMPRESA_LABELS).map(([key, label]) => {
            const ativo = editPapeis.includes(key);
            return (
              <button
                key={key}
                onClick={() => setEditPapeis((prev) => (ativo ? prev.filter((p) => p !== key) : [...prev, key]))}
                className={`inline-flex items-center gap-1 text-xs font-semibold rounded-full px-3 py-1.5 border cursor-pointer whitespace-nowrap transition-colors ${
                  ativo ? 'bg-[#E8610A] text-white border-[#E8610A]' : 'bg-white text-gray-600 border-gray-200 hover:border-[#E8610A]'
                }`}
              >
                {ativo && <i className="ri-check-line"></i>}
                {label}
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-[11px] text-gray-500 font-semibold">Origem / indicação</label>
            <select value={editOrigem} onChange={(e) => setEditOrigem(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white cursor-pointer focus:outline-none">
              <option value="">—</option>
              {Object.entries(ORIGEM_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div>
            <p className="text-[11px] text-gray-500 font-semibold mb-1">Papéis atuais</p>
            {editPapeis.length === 0 ? (
              <p className="text-sm text-gray-400">Nenhum papel definido.</p>
            ) : (
              <div className="flex items-center gap-1 flex-wrap">
                {editPapeis.map((p) => (
                  <span key={p} className="text-[11px] font-semibold text-[#E8610A] bg-[#E8610A]/10 rounded-full px-2 py-0.5">
                    {PAPEL_EMPRESA_LABELS[p] ?? p}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Inteligência de Relacionamento (scores + override) */}
      <InteligenciaRelacionamento empresaId={empresaId} password={password} />

      {/* Oportunidades */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <h3 className="text-sm font-bold text-gray-900 mb-4">Oportunidades</h3>
        {empresa.oportunidades?.length > 0 ? (
          <div className="space-y-2">
            {empresa.oportunidades.map((o: OportunidadeRow) => (
              <div key={o.id} className="flex items-center justify-between gap-3 border border-gray-100 rounded-xl p-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{o.titulo || empresa.nome}</p>
                  <p className="text-xs text-gray-500">{formatBRL(o.valor_potencial)}</p>
                </div>
                <select
                  value={o.etapa}
                  onChange={(e) => moveEtapa(o.id, e.target.value)}
                  className="text-xs font-semibold border rounded-full px-3 py-1.5 cursor-pointer focus:outline-none bg-white"
                >
                  {CRM_ETAPAS.map((e) => (
                    <option key={e.key} value={e.key}>{e.label}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-400">Nenhuma oportunidade registrada ainda.</p>
        )}
      </div>

      {/* Contatos */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <h3 className="text-sm font-bold text-gray-900 mb-4">Contatos e decisores</h3>
        {empresa.contatos?.length > 0 ? (
          <div className="space-y-2 mb-4">
            {empresa.contatos.map((c: ContatoRow) => (
              <div key={c.id} className="flex items-center justify-between gap-3 border border-gray-100 rounded-xl p-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Link to={`/painel/contatos/${c.id}`} className="text-sm font-semibold text-gray-900 truncate hover:text-[#E8610A] cursor-pointer">{c.nome || 'Sem nome'}</Link>
                    {c.nivel_decisao && (
                      <span className="text-[10px] font-semibold text-white bg-[#1F2329] rounded-full px-2 py-0.5 whitespace-nowrap">
                        {NIVEL_DECISAO_LABELS[c.nivel_decisao] ?? c.nivel_decisao}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 truncate">
                    {[c.cargo, c.departamento].filter(Boolean).join(' · ') || 'Sem cargo'}
                  </p>
                  {c.observacao && <p className="text-xs text-gray-400 mt-0.5 truncate">{c.observacao}</p>}
                </div>
                <div className="text-xs text-gray-500 flex items-center gap-3 shrink-0">
                  {c.telefone && (
                    <span className="inline-flex items-center gap-1 whitespace-nowrap"><i className="ri-phone-line"></i>{c.telefone}</span>
                  )}
                  {c.whatsapp && (
                    <span className="inline-flex items-center gap-1 whitespace-nowrap text-green-600"><i className="ri-whatsapp-line"></i>{c.whatsapp}</span>
                  )}
                  {c.email && (
                    <span className="truncate max-w-[140px] inline-flex items-center gap-1"><i className="ri-mail-line"></i>{c.email}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-400 mb-4">Nenhum contato ainda.</p>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          <input
            type="text"
            value={newNome}
            onChange={(e) => setNewNome(e.target.value)}
            placeholder="Nome *"
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A]"
          />
          <input
            type="text"
            value={newCargo}
            onChange={(e) => setNewCargo(e.target.value)}
            placeholder="Cargo"
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A]"
          />
          <select
            value={newNivelDecisao}
            onChange={(e) => setNewNivelDecisao(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white cursor-pointer focus:outline-none"
          >
            <option value="">Nível de decisão…</option>
            {Object.entries(NIVEL_DECISAO_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <input
            type="text"
            value={newDepartamento}
            onChange={(e) => setNewDepartamento(e.target.value)}
            placeholder="Departamento"
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A]"
          />
          <input
            type="text"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder="E-mail"
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A]"
          />
          <input
            type="text"
            value={newTelefone}
            onChange={(e) => setNewTelefone(e.target.value)}
            placeholder="Telefone"
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A]"
          />
          <input
            type="text"
            value={newWhatsapp}
            onChange={(e) => setNewWhatsapp(e.target.value)}
            placeholder="WhatsApp"
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A]"
          />
          <input
            type="text"
            value={newLinkedin}
            onChange={(e) => setNewLinkedin(e.target.value)}
            placeholder="LinkedIn"
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A]"
          />
          <input
            type="text"
            value={newObservacao}
            onChange={(e) => setNewObservacao(e.target.value)}
            placeholder="Observação"
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A]"
          />
        </div>
        <div className="mt-2 flex justify-end">
          <button
            onClick={addContato}
            disabled={addingContato || !newNome.trim()}
            className="inline-flex items-center justify-center gap-1 bg-[#E8610A] text-white text-sm font-semibold px-5 py-2 rounded-lg hover:bg-[#C4500A] cursor-pointer whitespace-nowrap disabled:opacity-60"
          >
            {addingContato ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-add-line"></i>}
            Adicionar contato
          </button>
        </div>
      </div>

      {/* Obras e Radar relacionados */}
      <ClienteObrasSection empresaId={empresaId} password={password} />
      <ClienteRadarSection empresaId={empresaId} password={password} />

      {/* Tabs */}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <div className="border-b border-gray-100 px-2">
          <div className="flex gap-1 overflow-x-auto">
            {TAB_CONFIG.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`inline-flex items-center gap-1.5 px-4 py-3 text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors border-b-2 ${
                  activeTab === tab.key
                    ? 'border-[#E8610A] text-[#E8610A]'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <i className={tab.icon}></i>
                {tab.label}
                {tab.count !== undefined && tab.count > 0 && (
                  <span className="bg-gray-100 text-gray-600 rounded-full px-1.5 py-0.5 text-[10px]">
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="p-6">
          {activeTab === 'timeline' && (
            <div>
              <div className="flex items-center justify-between gap-3 mb-4">
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Atividades e tarefas</h4>
                <button
                  onClick={sugerirProximaAcao}
                  disabled={sugerindo}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#E8610A] hover:text-[#C4500A] px-2 py-1 rounded-lg hover:bg-[#E8610A]/5 cursor-pointer whitespace-nowrap disabled:opacity-60"
                >
                  <i className={`ri-sparkling-2-line ${sugerindo ? 'animate-pulse' : ''}`}></i>
                  {sugerindo ? 'Pensando...' : 'Sugerir próxima ação'}
                </button>
              </div>

              {sugestao && (
                <div className="mb-4 bg-[#E8610A]/5 border border-[#E8610A]/20 rounded-xl p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900">{sugestao.proxima_acao}</p>
                      {sugestao.motivo && <p className="text-xs text-gray-600 mt-1">{sugestao.motivo}</p>}
                    </div>
                    <button
                      onClick={agendarSugestao}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-[#E8610A] hover:bg-[#C4500A] px-3 py-2 rounded-lg cursor-pointer whitespace-nowrap shrink-0"
                    >
                      <i className="ri-add-line"></i>
                      Agendar
                    </button>
                  </div>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-2 mb-4">
                <select
                  value={newTipo}
                  onChange={(e) => setNewTipo(e.target.value)}
                  className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white cursor-pointer"
                >
                  {Object.entries(ATIVIDADE_TIPOS).map(([k, v]) => (
                    <option key={k} value={k}>{v.label}</option>
                  ))}
                </select>
                <input
                  type="text"
                  value={newAtividade}
                  onChange={(e) => setNewAtividade(e.target.value)}
                  placeholder="Ex: Ligar para confirmar reunião..."
                  className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A]"
                />
                <button
                  onClick={addAtividade}
                  disabled={addingAtividade || !newAtividade.trim()}
                  className="inline-flex items-center justify-center gap-1 bg-[#E8610A] text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-[#C4500A] cursor-pointer whitespace-nowrap disabled:opacity-60"
                >
                  {addingAtividade ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-add-line"></i>}
                </button>
              </div>

              {atividades.length === 0 ? (
                <p className="text-sm text-gray-400">Nenhuma atividade registrada.</p>
              ) : (
                <div className="space-y-2">
                  {atividades.map((a) => {
                    const tipo = ATIVIDADE_TIPOS[a.tipo] ?? ATIVIDADE_TIPOS.nota;
                    return (
                      <div key={a.id} className="flex items-start gap-3 border border-gray-100 rounded-xl p-3">
                        <button
                          onClick={() => toggleAtividade(a.id, !a.done)}
                          className={`w-6 h-6 shrink-0 flex items-center justify-center border rounded-full cursor-pointer ${
                            a.done ? 'bg-green-500 border-green-500 text-white' : 'border-gray-300 text-transparent hover:border-green-500'
                          }`}
                        >
                          <i className="ri-check-line text-xs"></i>
                        </button>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="inline-flex items-center gap-1 text-xs font-semibold text-gray-600 bg-gray-100 rounded-full px-2 py-0.5">
                              <i className={tipo.icon}></i>
                              {tipo.label}
                            </span>
                            <span className="text-xs text-gray-400">{formatDateTime(a.data)}</span>
                          </div>
                          <p className={`text-sm text-gray-700 mt-1 ${a.done ? 'line-through text-gray-400' : ''}`}>
                            {a.conteudo}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {activeTab === 'comunicacoes' && (
            <TimelineComunicacoes empresaId={empresaId} onDataChanged={load} />
          )}

          {activeTab === 'propostas' && (
            <div>
              {quotes.length === 0 ? (
                <p className="text-sm text-gray-400 py-4">Nenhuma proposta ainda.</p>
              ) : (
                <div className="space-y-2">
                  {quotes.map((q) => (
                    <div key={q.id} className="flex items-center justify-between gap-3 border border-gray-100 rounded-xl p-3">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900">Orçamento #{q.id}</p>
                        <p className="text-xs text-gray-500">{formatDate(q.created_at)} · {q.status}</p>
                      </div>
                      <p className="text-sm font-bold text-gray-900 whitespace-nowrap">{formatBRL(Number(q.total ?? 0))}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'pedidos' && (
            <div>
              {orders.length === 0 ? (
                <p className="text-sm text-gray-400 py-4">Nenhum pedido ainda.</p>
              ) : (
                <div className="space-y-2">
                  {orders.map((o) => (
                    <div key={o.id} className="flex items-center justify-between gap-3 border border-gray-100 rounded-xl p-3">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900">Pedido #{o.id}</p>
                        <p className="text-xs text-gray-500">{formatDate(o.created_at)} · {o.status}</p>
                      </div>
                      <p className="text-sm font-bold text-gray-900 whitespace-nowrap">{formatBRL(Number(o.subtotal_items ?? 0))}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Próximos módulos */}
      <div className="bg-white rounded-2xl border border-dashed border-gray-300 p-6">
        <div className="flex items-center gap-2 mb-1">
          <i className="ri-stack-line text-gray-400"></i>
          <h3 className="text-sm font-bold text-gray-700">Próximos módulos da Conta 360º</h3>
        </div>
        <p className="text-xs text-gray-400 mb-4">
          Estrutura já preparada para receber estes relacionamentos gradualmente, conforme os dados forem surgindo.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {FUTURO_MODULOS.map((m) => (
            <div key={m.label} className="border border-gray-100 rounded-xl p-3 bg-gray-50/50">
              <div className="w-8 h-8 flex items-center justify-center bg-gray-100 text-gray-400 rounded-lg mb-2">
                <i className={`${m.icon} text-base`}></i>
              </div>
              <p className="text-sm font-semibold text-gray-600">{m.label}</p>
              <p className="text-xs text-gray-400 mt-0.5 leading-relaxed">{m.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}